import React, { useState, useEffect, useLayoutEffect, useContext, useRef, useCallback } from 'react';
import {
  registerTickerModalPrimeInput,
  unregisterTickerModalPrimeInput,
} from '../../utils/tickerModalKeyboard';
import { Modal, FormControl, Badge, InputGroup } from 'react-bootstrap';
import { TradingContext } from '../../contexts/TradingContext';
import { StreamsContext } from '../../contexts/StreamsContext';
import { AppStateContext } from '../../contexts/AppStateContext';
import { useGetTicker } from '../../hooks/useTradeStation';
import { useTickerSearch } from '../../hooks/useTickerSearch';
import { colors } from '../../theme/colors';
import { isBaseFuturesProduct, getCurrentContractSymbol, getNextContractMonth, resolveTradableSymbol } from '../../utils/futuresContracts';
import { Search } from 'react-bootstrap-icons';

const TickerInputModal = () => {
  const [show, setShow] = useState(false);
  const [inputTicker, setInputTicker] = useState('');
  const [invalidTicker, setInvalidTicker] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [activeChartId, setActiveChartId] = useState(null); // Track which chart opened the modal
  const [selectedIndex, setSelectedIndex] = useState(-1); // For keyboard navigation
  const [mode, setMode] = useState('chart'); // 'chart' or 'watchlist'
  const [onAddCallback, setOnAddCallback] = useState(null); // Callback for watchlist mode
  const inputRef = useRef(null);
  const primeInputRef = useRef(null);
  const resultsRef = useRef(null);

  useEffect(() => {
    registerTickerModalPrimeInput(primeInputRef.current);
    return () => unregisterTickerModalPrimeInput();
  }, []);

  /** Focus the visible search field; retry because modal mount timing varies on mobile. */
  const focusSearchInput = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const attempt = () => {
      try {
        el.focus({ preventScroll: true });
        if (typeof el.setSelectionRange === 'function') {
          const len = el.value.length;
          el.setSelectionRange(len, len);
        }
      } catch (_) {
        // ignore
      }
    };
    attempt();
    requestAnimationFrame(() => {
      attempt();
      requestAnimationFrame(attempt);
    });
    [50, 150].forEach((ms) => setTimeout(attempt, ms));
  }, []);

  useLayoutEffect(() => {
    if (show) focusSearchInput();
  }, [show, focusSearchInput]);

  const { getTicker } = useGetTicker();
  const searchResults = useTickerSearch(show ? inputTicker : '', { enabled: show });
  const { setSelectedTickerDetails } = useContext(TradingContext);
  const { selectedTicker, activeChartId: contextActiveChartId } = useContext(AppStateContext);
  const { setMarketAggregates } = useContext(StreamsContext);
  const { setStopLoss, setLimitPrice, setTakeProfitPrices, setTpInitialized, updateChartSettings, getChart } = useContext(AppStateContext);

  // Listen for ticker:open-modal event (chart ticker change)
  useEffect(() => {
    const handleTickerOpen = (event) => {
      const chartId = event.detail?.chartId;
      setActiveChartId(chartId || contextActiveChartId); // Use provided chartId or fallback to context activeChartId
      
      // Get the ticker for the specific chart
      const chart = chartId ? getChart(chartId) : null;
      const chartTicker = chart?.ticker || selectedTicker || '';
      
      setInputTicker('');
      setInvalidTicker(false);
      setErrorMessage('');
      setSelectedIndex(-1);
      setMode('chart');
      setOnAddCallback(null);
      setShow(true);
    };
    window.addEventListener('ticker:open-modal', handleTickerOpen);
    return () => {
      window.removeEventListener('ticker:open-modal', handleTickerOpen);
    };
  }, [selectedTicker, contextActiveChartId, getChart]);

  // Listen for ticker:add-to-watchlist event (add to watchlist)
  useEffect(() => {
    const handleAddToWatchlist = (event) => {
      const callback = event.detail?.onAdd;
      setInputTicker('');
      setInvalidTicker(false);
      setErrorMessage('');
      setSelectedIndex(-1);
      setMode('watchlist');
      setOnAddCallback(() => callback); // Store callback
      setShow(true);
    };
    window.addEventListener('ticker:add-to-watchlist', handleAddToWatchlist);
    return () => {
      window.removeEventListener('ticker:add-to-watchlist', handleAddToWatchlist);
    };
  }, []);

  useEffect(() => {
    setSelectedIndex(-1);
  }, [inputTicker]);

  const handleInputChange = (raw) => {
    // Auto-capitalize input
    const value = (raw || '').toUpperCase();
    setInputTicker(value);
    setInvalidTicker(false);
    setErrorMessage('');
  };

  const commitTicker = async (tickerSymbol) => {
    const cleaned = (tickerSymbol || '').trim().toUpperCase();
    const actualSymbol = resolveTradableSymbol(cleaned);
    const isFuturesBase = isBaseFuturesProduct(cleaned);
    const contractInfo = isFuturesBase ? getNextContractMonth() : null;
    if (isFuturesBase) {
      console.log(`🔄 [TickerInputModal] Auto-calculating futures contract: ${cleaned} → ${actualSymbol} (${contractInfo.monthName} ${contractInfo.year})`);
    }
    
    if (!actualSymbol) {
      setShow(false);
      return;
    }

    // If in watchlist mode, just validate and call the callback
    if (mode === 'watchlist') {
      // Clear any previous error state and start validating
      setInvalidTicker(false);
      setIsValidating(true);

      try {
        const data = await getTicker(actualSymbol);
        
        if (!data || data.Errors?.length || !Array.isArray(data.Symbols) || data.Symbols.length === 0) {
          setInvalidTicker(true);
          setErrorMessage(isFuturesBase 
            ? `Contract ${actualSymbol} not found. The ${contractInfo.monthName} ${contractInfo.year} contract may not be available yet.`
            : 'Invalid ticker symbol'
          );
          setIsValidating(false);
          return;
        }

        // Valid ticker - call the callback
        if (onAddCallback) {
          await onAddCallback(actualSymbol);
        }

        setShow(false);
        setIsValidating(false);
      } catch (error) {
        console.error('Error validating ticker:', error);
        setInvalidTicker(true);
        setErrorMessage('Error validating ticker');
        setIsValidating(false);
      }
      return;
    }
    
    // Chart mode - change chart ticker
    // Get the current ticker for the active chart
    const activeChart = activeChartId ? getChart(activeChartId) : null;
    const currentChartTicker = activeChart?.ticker || selectedTicker;
    
    if (actualSymbol === currentChartTicker) {
      setShow(false);
      return;
    }

    // Clear any previous error state and start validating
    setInvalidTicker(false);
    setIsValidating(true);

    try {
      const data = await getTicker(actualSymbol);
      
      if (!data || data.Errors?.length || !Array.isArray(data.Symbols) || data.Symbols.length === 0) {
        setInvalidTicker(true);
        setErrorMessage(isFuturesBase 
          ? `Contract ${actualSymbol} not found. The ${contractInfo.monthName} ${contractInfo.year} contract may not be available yet.`
          : 'Invalid ticker symbol'
        );
        setIsValidating(false);
        return;
      }

      const tickerDetails = data.Symbols[0];
      const chartSymbol = tickerDetails.Symbol || actualSymbol;

      // Update the specific chart's ticker (the one that opened the modal)
      if (activeChartId) {
        await updateChartSettings(activeChartId, { ticker: chartSymbol });
      }
      setSelectedTickerDetails(tickerDetails);
      // Reset dependent stream state so effects re-seed from valid quote
      setMarketAggregates({ bids: [], asks: [] });
      setStopLoss(0);
      setLimitPrice(0);
      setTakeProfitPrices([]);
      setTpInitialized(false);

      setShow(false);
      setIsValidating(false);
    } catch (error) {
      console.error('Error validating ticker:', error);
      setInvalidTicker(true);
      setErrorMessage('Error validating ticker');
      setIsValidating(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      // If a search result is selected, use that ticker
      if (selectedIndex >= 0 && searchResults[selectedIndex]) {
        commitTicker(searchResults[selectedIndex].symbol);
      } else if (inputTicker.trim()) {
        // Otherwise, validate the raw input
        commitTicker(inputTicker);
      }
    } else if (e.key === 'Escape') {
      setShow(false);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => 
        prev < searchResults.length - 1 ? prev + 1 : prev
      );
      // Scroll selected item into view
      if (resultsRef.current && selectedIndex < searchResults.length - 1) {
        const nextIndex = selectedIndex + 1;
        const item = resultsRef.current.children[nextIndex];
        if (item) {
          item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
      // Scroll selected item into view
      if (resultsRef.current && selectedIndex > 0) {
        const prevIndex = selectedIndex - 1;
        const item = resultsRef.current.children[prevIndex];
        if (item) {
          item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }
    }
  };

  const handleClose = () => {
    setShow(false);
  };

  const getTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'futures': return '#FFA500';
      case 'etf': return '#00BFFF';
      case 'stock': return '#32CD32';
      default: return colors.textLight;
    }
  };

  const getTypeBadge = (type) => {
    const typeUpper = type?.toUpperCase() || 'STOCK';
    return (
      <Badge 
        style={{ 
          backgroundColor: getTypeColor(type), 
          color: '#000',
          fontSize: '9px',
          padding: '2px 6px',
          fontWeight: 600
        }}
      >
        {typeUpper}
      </Badge>
    );
  };

  return (
    <>
      {/* Focused synchronously on ticker tap (same gesture) so iOS keeps the keyboard session */}
      <input
        ref={primeInputRef}
        type="text"
        inputMode="text"
        autoComplete="off"
        aria-hidden="true"
        tabIndex={-1}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: 1,
          height: 1,
          opacity: 0,
          pointerEvents: 'none',
          fontSize: 16,
        }}
      />
    <Modal
      show={show}
      onHide={handleClose}
      animation={false}
      centered
      scrollable
      enforceFocus={false}
      restoreFocus={false}
      className="ticker-input-chart-modal"
      contentClassName="bg-dark"
      size="md"
      dialogClassName="ticker-input-chart-modal-dialog"
      onShow={focusSearchInput}
      onEntered={focusSearchInput}
    >
      <Modal.Header 
        closeButton
        style={{ 
          backgroundColor: colors.bgPrimary, 
          borderBottom: `1px solid ${colors.border}`,
          color: colors.text
        }}
      >
        <Modal.Title style={{ fontSize: '18px', fontWeight: 600 }}>
          {mode === 'watchlist' ? 'Add Ticker to Watchlist' : 'Change Chart Ticker'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ backgroundColor: colors.bgPrimary, padding: '20px' }}>
        <style>
          {`
            .ticker-search-input::placeholder {
              color: #a0a0a0 !important;
              opacity: 0.7;
            }
          `}
        </style>
        
        <InputGroup>
          <InputGroup.Text 
            style={{ 
              backgroundColor: colors.bgSecondary,
              border: `1px solid ${invalidTicker ? colors.danger : colors.borderLight}`,
              borderRight: 'none',
              color: colors.textLight,
              paddingLeft: '12px',
              paddingRight: '8px'
            }}
          >
            <Search size={18} />
          </InputGroup.Text>
          <FormControl 
            ref={inputRef}
            value={inputTicker}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            isInvalid={invalidTicker}
            disabled={isValidating}
            placeholder="Type any ticker (e.g., AAPL, MNQ)"
            autoComplete="off"
            className="ticker-search-input"
            style={{
              backgroundColor: colors.bgSecondary,
              border: `1px solid ${invalidTicker ? colors.danger : colors.borderLight}`,
              borderLeft: 'none',
              color: colors.white,
              fontSize: '16px',
              padding: '12px',
              fontWeight: 500
            }}
          />
        </InputGroup>
        
        {/* Search Results */}
        {searchResults.length > 0 && !isValidating && (
          <div 
            ref={resultsRef}
            style={{
              marginTop: '12px',
              maxHeight: '300px',
              overflowY: 'auto',
              backgroundColor: colors.bgSecondary,
              borderRadius: '8px',
              border: `1px solid ${colors.borderLight}`
            }}
          >
            {searchResults.map((result, index) => {
              const isFuturesBase = isBaseFuturesProduct(result.symbol);
              const actualContract = isFuturesBase ? getCurrentContractSymbol(result.symbol) : null;
              const contractInfo = isFuturesBase ? getNextContractMonth() : null;
              
              return (
                <div
                  key={result.symbol}
                  onClick={() => commitTicker(result.symbol)}
                  onMouseEnter={() => setSelectedIndex(index)}
                  style={{
                    padding: '10px 12px',
                    cursor: 'pointer',
                    backgroundColor: selectedIndex === index ? colors.primary + '20' : 'transparent',
                    borderBottom: index < searchResults.length - 1 ? `1px solid ${colors.border}` : 'none',
                    transition: 'background-color 0.15s ease'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                        <span style={{ 
                          color: colors.white, 
                          fontWeight: 700, 
                          fontSize: '14px',
                          letterSpacing: '0.3px'
                        }}>
                          {result.symbol}
                        </span>
                        {getTypeBadge(result.type)}
                        {isFuturesBase && (
                          <span style={{
                            fontSize: '10px',
                            color: colors.textLight,
                            opacity: 0.7,
                            fontWeight: 500
                          }}>
                            → {actualContract}
                          </span>
                        )}
                      </div>
                      <div style={{ 
                        color: colors.textLight, 
                        fontSize: '12px',
                        opacity: 0.8
                      }}>
                        {result.name}
                        {isFuturesBase && contractInfo && (
                          <span style={{ 
                            marginLeft: '6px',
                            fontSize: '11px',
                            opacity: 0.7,
                            fontStyle: 'italic'
                          }}>
                            ({contractInfo.monthName} {contractInfo.year} contract)
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {/* No results message */}
        {inputTicker.trim().length > 0 && searchResults.length === 0 && !isValidating && (
          <div style={{ 
            marginTop: '12px', 
            color: colors.textLight, 
            fontSize: '12px', 
            textAlign: 'center',
            padding: '12px',
            opacity: 0.7
          }}>
            Press Enter to validate "{inputTicker}"
          </div>
        )}
        
        {/* Error message */}
        {invalidTicker && errorMessage && (
          <div style={{ marginTop: '12px', color: colors.danger, fontSize: '12px', textAlign: 'center' }}>
            {errorMessage}
          </div>
        )}
        
        {/* Validating message */}
        {isValidating && (
          <div style={{ marginTop: '12px', color: colors.textLight, fontSize: '12px', textAlign: 'center' }}>
            Validating ticker...
          </div>
        )}
        
        {/* Hint text */}
        {!isValidating && (
          <div style={{ 
            marginTop: '12px', 
            color: colors.textLight, 
            fontSize: '11px', 
            textAlign: 'center',
            opacity: 0.5
          }}>
            ↑↓ navigate • Enter select • Esc cancel
          </div>
        )}
      </Modal.Body>
    </Modal>
    </>
  );
};

export default TickerInputModal;
